import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Star, Heart, Hammer, BookOpen, Image, ExternalLink, Youtube, Download, UserPlus, UserCheck, Trophy, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ count, size = 'h-3.5 w-3.5' }) => (
  <span className="flex">{[1,2,3,4,5].map(i => <Star key={i} className={`${size} ${i <= count ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`} />)}</span>
);

const BuilderProfile = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const userId = params.userId || searchParams.get('userId');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('ideas');
  const [followData, setFollowData] = useState({ count: 0, is_following: false });
  const [trophies, setTrophies] = useState([]);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/builder/${userId}`, { headers: authHeaders });
        setProfile(res.data);
      } catch { setProfile(null); }
      setLoading(false);
    };
    const fetchFollow = async () => {
      try {
        const res = await axios.get(`${API}/builders/${userId}/followers`, { headers: authHeaders });
        setFollowData(res.data);
      } catch { /* empty */ }
    };
    fetchProfile();
    fetchFollow();
    // Fetch trophies
    axios.get(`${API}/builder/${userId}/trophies`, { headers: authHeaders })
      .then(res => setTrophies(res.data || [])).catch(() => {});
  }, [userId]);

  if (loading) return <div className="bg-amber-50 flex items-center justify-center text-stone-400">Loading...</div>;

  const toggleFollow = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/builders/${userId}/follow`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setFollowData(prev => ({ count: res.data.following ? prev.count + 1 : Math.max(prev.count - 1, 0), is_following: res.data.following }));
      toast.success(res.data.following ? 'Following!' : 'Unfollowed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (!profile) return (
    <div className="bg-amber-50 flex items-center justify-center">
      <div className="text-center space-y-3">
        <p className="text-stone-500">This builder profile is not available.</p>
        <button onClick={() => navigate('/diy-ideas')} className="text-amber-600 text-sm font-medium hover:underline">Back to DIY Ideas</button>
      </div>
    </div>
  );

  const s = profile.stats;
  const getPhotoPath = (p) => typeof p === 'string' ? p : p?.path || '';

  return (
    <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 pb-24">
        <button onClick={() => navigate('/diy-ideas')} className="flex items-center gap-2 text-stone-600 hover:text-stone-800 mb-4 text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to DIY Ideas
        </button>

        {/* Profile Header */}
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-2xl flex-shrink-0">
              {profile.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-stone-800">{profile.name}</h1>
              {profile.company && <p className="text-sm text-stone-500">{profile.company}</p>}
              {profile.bio && <p className="text-sm text-stone-600 mt-2">{profile.bio}</p>}
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs text-stone-500">{followData.count} follower{followData.count !== 1 ? 's' : ''}</span>
                {user?.id !== userId && (
                  <button onClick={toggleFollow} className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full font-medium transition ${followData.is_following ? 'bg-stone-200 text-stone-700' : 'bg-amber-600 text-white hover:bg-amber-700'}`}>
                    {followData.is_following ? <><UserCheck className="h-3.5 w-3.5" /> Following</> : <><UserPlus className="h-3.5 w-3.5" /> Follow</>}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5">
            {[
              { label: 'Ideas Shared', value: s.total_ideas, icon: BookOpen },
              { label: 'Made It', value: s.total_made_its, icon: Hammer },
              { label: 'Total Likes', value: s.total_likes, icon: Heart },
              { label: 'Reviews', value: s.total_reviews, icon: Star },
              { label: 'Avg Rating', value: s.avg_rating > 0 ? s.avg_rating : '—', icon: Star },
            ].map(stat => (
              <div key={stat.label} className="bg-stone-50 rounded-xl p-3 text-center">
                <stat.icon className="h-4 w-4 mx-auto text-amber-600 mb-1" />
                <div className="text-lg font-bold text-stone-800">{stat.value}</div>
                <div className="text-[10px] text-stone-500">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Trophies */}
          {trophies.length > 0 && (
            <div className="mt-4 pt-4 border-t border-stone-100">
              <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-1.5 mb-2"><Trophy className="h-4 w-4 text-amber-500" /> Challenge Wins ({trophies.length})</h3>
              <div className="flex flex-wrap gap-2">
                {trophies.map(t => (
                  <div key={t.id} className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-xs">
                    <Trophy className="h-3.5 w-3.5 text-amber-500 inline mr-1" />
                    <span className="font-medium text-amber-800">{t.challenge?.title || 'Challenge'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {[
            { id: 'ideas', label: `Ideas (${profile.ideas?.length || 0})` },
            { id: 'made_its', label: `Made It (${profile.made_its?.length || 0})` },
            { id: 'collections', label: `Collections (${profile.collections?.length || 0})` },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`text-sm px-4 py-2 rounded-full font-medium transition ${tab === t.id ? 'bg-amber-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-50 border border-stone-200'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Ideas Tab */}
        {tab === 'ideas' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(profile.ideas || []).map(idea => (
              <div key={idea.id} onClick={() => navigate(`/diy-ideas/${idea.id}`)} className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden hover:shadow-md transition cursor-pointer">
                {(idea.photos || []).length > 0 ? (
                  <img src={`${API}/files/${getPhotoPath(idea.photos[0])}`} alt="" className="w-full h-36 object-cover" />
                ) : (
                  <div className="w-full h-24 bg-amber-50 flex items-center justify-center"><Image className="h-8 w-8 text-amber-300" /></div>
                )}
                <div className="p-3">
                  <h3 className="font-bold text-sm text-stone-800 mb-1">{idea.title}</h3>
                  {idea.review_count > 0 && <div className="flex items-center gap-1"><Stars count={Math.round(idea.review_avg)} size="h-3 w-3" /><span className="text-xs text-stone-500">{idea.review_avg}</span></div>}
                  <div className="flex items-center gap-2 mt-1 text-xs text-stone-400">
                    <span><Heart className="h-3 w-3 inline" /> {idea.likes || 0}</span>
                    {idea.made_it_count > 0 && <span><Hammer className="h-3 w-3 inline" /> {idea.made_it_count}</span>}
                  </div>
                </div>
              </div>
            ))}
            {(profile.ideas || []).length === 0 && <p className="text-sm text-stone-400 italic col-span-3">No ideas shared yet</p>}
          </div>
        )}

        {/* Made Its Tab */}
        {tab === 'made_its' && (
          <div className="space-y-3">
            {(profile.made_its || []).map(m => (
              <div key={m.id} className="bg-white rounded-2xl border border-stone-200 shadow-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-stone-400">{(m.created_at || '').split('T')[0]}</span>
                  {m.stars > 0 && <Stars count={m.stars} size="h-3 w-3" />}
                </div>
                {m.caption && <p className="text-sm text-stone-700 mb-2">{m.caption}</p>}
                {m.photos?.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto">
                    {m.photos.map((ph, i) => <img key={i} src={`${API}/files/${typeof ph === 'string' ? ph : ph?.path || ''}`} alt="" className="h-24 w-24 object-cover rounded-xl flex-shrink-0 cursor-pointer hover:opacity-80 transition" onClick={() => setLightboxPhoto(`${API}/files/${typeof ph === 'string' ? ph : ph?.path || ''}`)} />)}
                  </div>
                )}
              </div>
            ))}
            {(profile.made_its || []).length === 0 && <p className="text-sm text-stone-400 italic">No builds shared yet</p>}
          </div>
        )}

        {/* Collections Tab */}
        {tab === 'collections' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(profile.collections || []).map(col => (
              <div key={col.id} onClick={() => navigate(`/diy-collections/${col.id}`)} className="bg-white rounded-2xl border border-stone-200 shadow-sm p-4 hover:shadow-md transition cursor-pointer">
                <h3 className="font-bold text-stone-800">{col.name}</h3>
                {col.description && <p className="text-xs text-stone-500 mt-1">{col.description}</p>}
                <span className="text-xs text-amber-600 mt-2 inline-block">{(col.idea_ids || []).length} ideas</span>
              </div>
            ))}
            {(profile.collections || []).length === 0 && <p className="text-sm text-stone-400 italic">No collections yet</p>}
          </div>
        )}
      </div>

      {/* Photo Lightbox */}
      {lightboxPhoto && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setLightboxPhoto(null)} data-testid="photo-lightbox">
          <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightboxPhoto(null)} className="absolute -top-10 right-0 text-white hover:text-stone-300"><X className="h-6 w-6" /></button>
            <img src={lightboxPhoto} alt="" className="w-full max-h-[80vh] object-contain rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default BuilderProfile;
